const colors = {
  primary: [
    {
      name: "Kin Green",
      hex: "var(--color-kin-green)",
      className: "bg-kin-green",
      role: "Primary actions and positive confidence cues",
      light: false,
    },
    {
      name: "Leaf Deep",
      hex: "var(--color-kin-green-dark)",
      className: "bg-kin-green-dark",
      role: "Depth, pressed states, and high emphasis",
      light: false,
    },
    {
      name: "Mint Light",
      hex: "var(--color-kin-green-light)",
      className: "bg-kin-green-light",
      role: "Soft highlights and supportive tint surfaces",
      light: true,
    },
  ],
  secondary: [
    {
      name: "Sun Gold",
      hex: "var(--color-kin-gold)",
      className: "bg-kin-gold",
      role: "Highlights, recommendations, and key guidance moments",
      light: false,
    },
    {
      name: "Sunshine",
      hex: "var(--color-kin-gold-light)",
      className: "bg-kin-gold-light",
      role: "Ambient warmth and lighter guidance surfaces",
      light: true,
    },
  ],
  accents: [
    {
      name: "Tangerine",
      hex: "var(--color-kin-orange)",
      className: "bg-kin-orange",
      role: "Warnings, review states, and friction that needs attention",
      light: false,
    },
    {
      name: "Peach",
      hex: "var(--color-kin-orange-light)",
      className: "bg-kin-orange-light",
      role: "Warm fills for softer attention moments",
      light: true,
    },
    {
      name: "Wildflower",
      hex: "#A590FF",
      className: "bg-kin-lavender",
      role: "AI, analysis, and insight-oriented emphasis",
      light: false,
    },
    {
      name: "Lilac Mist",
      hex: "var(--color-kin-lavender-light)",
      className: "bg-kin-lavender-light",
      role: "Soft feature surfaces and AI-related context",
      light: true,
    },
  ],
  neutrals: [
    {
      name: "Ink",
      hex: "var(--color-kin-black)",
      className: "bg-kin-black",
      role: "Headlines and contrast-critical text",
      light: false,
    },
    {
      name: "Evergreen Gray",
      hex: "#3A4A3D",
      className: "bg-kin-olive",
      role: "Borders, controls, and structural UI",
      light: false,
    },
    {
      name: "Body",
      hex: "var(--color-kin-body)",
      className: "bg-kin-body",
      role: "Default reading color",
      light: false,
    },
    {
      name: "Muted",
      hex: "#7E8B82",
      className: "bg-kin-muted",
      role: "Meta labels and low-emphasis details",
      light: false,
    },
  ],
  backgrounds: [
    {
      name: "Parchment",
      hex: "#FBF9F4",
      className: "bg-kin-cream",
      role: "Default page background with human warmth",
      light: true,
    },
    {
      name: "Warm Linen",
      hex: "#F6F4EE",
      className: "bg-kin-sage",
      role: "Section background with soft depth and less clinical chill",
      light: true,
    },
    {
      name: "Soft White",
      hex: "#FFFEFA",
      className: "bg-kin-white",
      role: "Cards, overlays, and elevated surfaces",
      light: true,
    },
  ],
};

const backgroundOptions = [
  {
    id: "A",
    name: "Warm Linen",
    cream: "#FAF8F5",
    sage: "#F5F3EF",
    white: "#FFFFFF",
    description: "Warm neutral. Clean, soft, and restrained.",
  },
  {
    id: "B",
    name: "Stone",
    cream: "#F7F7F5",
    sage: "#F2F2EF",
    white: "#FEFEFE",
    description: "Modernist neutral. Crisp and low-personality.",
  },
  {
    id: "C",
    name: "Cloud",
    cream: "#F9FAF9",
    sage: "#F3F5F3",
    white: "#FFFFFF",
    description: "Barely-there green hint. Cleaner than mint, but faint.",
  },
  {
    id: "D",
    name: "Parchment",
    cream: "#FBF9F4",
    sage: "#F6F4EE",
    white: "#FFFEFA",
    description: "Warm paper tone. Human, premium, distinctly not clinical.",
    selected: true,
  },
  {
    id: "E",
    name: "Frost",
    cream: "#F8FAFA",
    sage: "#F0F4F3",
    white: "#FFFFFF",
    description: "Cool whisper. Calm and airy, but slightly more technical.",
  },
];

const selectedDirection = {
  greens: {
    primary: "var(--color-kin-green)",
    deep: "var(--color-kin-green-dark)",
    light: "var(--color-kin-green-light)",
  },
  neutrals: {
    black: "var(--color-kin-black)",
    body: "var(--color-kin-body)",
    muted: "#7E8B82",
  },
  backgrounds: {
    cream: "#FBF9F4",
    panel: "#F6F4EE",
    white: "#FFFEFA",
  },
  accents: {
    gold: "var(--color-kin-gold)",
    lavender: "#A590FF",
    orange: "var(--color-kin-orange)",
    rose: "var(--color-kin-rose)",
  },
};

const accentStudies = [
  {
    label: "AI Context",
    title: "Lavender surfaces for analysis and assistant moments",
    body: "Use in rails, insight chips, and assistant context so AI is visible without tinting the entire interface.",
    color: selectedDirection.accents.lavender,
  },
  {
    label: "Guidance",
    title: "Gold for recommendations and supportive next steps",
    body: "Keep gold for guidance and elevated recommendations, not as a permanent layout color.",
    color: selectedDirection.accents.gold,
  },
  {
    label: "Review",
    title: "Orange for missing data, setup gaps, and follow-up prompts",
    body: "Orange should carry friction and review states so the interface has more life than green-on-neutral alone.",
    color: selectedDirection.accents.orange,
  },
  {
    label: "Flagged",
    title: "Rose for abnormal values and hard-stop trust signals",
    body: "Reserve rose for flagged states and risk surfacing so it remains meaningful and sharp.",
    color: selectedDirection.accents.rose,
  },
];

function Swatch({ color }) {
  return (
    <article className="kin-surface kin-surface-waves rounded-2xl p-3 sm:p-4">
      <div
        className={`h-20 rounded-xl border ${
          color.light ? "border-kin-olive/14" : "border-transparent"
        } ${color.className}`}
      />
      <p className="mt-3 text-sm font-semibold text-kin-black">{color.name}</p>
      <p className="mt-0.5 font-mono text-xs text-kin-muted">{color.hex}</p>
      <p className="mt-2 text-[11px] leading-relaxed text-kin-body/85">{color.role}</p>
    </article>
  );
}

function ColorGroup({ title, swatches }) {
  return (
    <section>
      <h3 className="text-xs font-semibold tracking-[0.2em] uppercase text-kin-muted">{title}</h3>
      <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {swatches.map((swatch) => (
          <Swatch key={swatch.hex} color={swatch} />
        ))}
      </div>
    </section>
  );
}

function TonePill({ label, color }) {
  return (
    <span
      style={{
        padding: "4px 10px",
        borderRadius: 999,
        background: `${color}16`,
        border: `1px solid ${color}30`,
        color,
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: "0.04em",
      }}
    >
      {label}
    </span>
  );
}

function BackgroundPreview({ option }) {
  return (
    <div
      style={{
        background: option.cream,
        border: "1px solid rgba(0,0,0,0.06)",
        borderRadius: 18,
        padding: 12,
      }}
    >
      <div
        style={{
          background: option.sage,
          border: "1px solid rgba(0,0,0,0.05)",
          borderRadius: 14,
          padding: 12,
        }}
      >
        <div className="flex flex-wrap gap-2">
          <TonePill label="AI" color={selectedDirection.accents.lavender} />
          <TonePill label="Doctor brief" color={selectedDirection.accents.gold} />
        </div>

        <div
          style={{
            background: option.white,
            border: "1px solid rgba(0,0,0,0.05)",
            borderRadius: 14,
            padding: 12,
            marginTop: 10,
          }}
        >
          <p
            style={{
              fontSize: 15,
              fontWeight: 700,
              fontFamily: "'Cormorant Garamond', serif",
              color: selectedDirection.neutrals.black,
            }}
          >
            Recovery Summary
          </p>
          <p style={{ fontSize: 11, color: selectedDirection.neutrals.body, lineHeight: 1.45 }}>
            The surface stack should feel premium, readable, and clearly layered.
          </p>

          <div className="mt-3 grid grid-cols-2 gap-2">
            <div
              style={{
                borderRadius: 10,
                padding: "9px 10px",
                background: `${selectedDirection.greens.primary}14`,
                border: `1px solid ${selectedDirection.greens.primary}24`,
              }}
            >
              <p
                style={{
                  fontSize: 9,
                  fontWeight: 700,
                  color: selectedDirection.greens.deep,
                  textTransform: "uppercase",
                  letterSpacing: "0.08em",
                }}
              >
                Ready
              </p>
              <p style={{ fontSize: 11, color: selectedDirection.neutrals.body }}>Run skill</p>
            </div>
            <div
              style={{
                borderRadius: 10,
                padding: "9px 10px",
                background: `${selectedDirection.accents.gold}14`,
                border: `1px solid ${selectedDirection.accents.gold}28`,
              }}
            >
              <p
                style={{
                  fontSize: 9,
                  fontWeight: 700,
                  color: "var(--color-kin-gold-dark)",
                  textTransform: "uppercase",
                  letterSpacing: "0.08em",
                }}
              >
                Share
              </p>
              <p style={{ fontSize: 11, color: selectedDirection.neutrals.body }}>Doctor brief</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function BackgroundOptionCard({ option }) {
  return (
    <article className="kin-surface rounded-3xl p-5">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="flex h-8 w-8 items-center justify-center rounded-xl bg-kin-green text-xs font-bold text-white">
            {option.id}
          </span>
          <div>
            <p className="text-sm font-semibold text-kin-black">{option.name}</p>
            <p className="text-[11px] text-kin-muted">{option.description}</p>
          </div>
        </div>
        {option.selected ? (
          <span className="rounded-full border border-kin-green/25 bg-kin-green/10 px-3 py-1 text-[10px] font-semibold text-kin-green-dark">
            Chosen Base
          </span>
        ) : null}
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        {[
          { label: "cream", hex: option.cream },
          { label: "sage", hex: option.sage },
          { label: "white", hex: option.white },
        ].map((swatch) => (
          <span
            key={swatch.label}
            className="inline-flex items-center gap-2 rounded-full border border-kin-olive/10 bg-kin-white/70 px-3 py-1.5"
          >
            <span
              style={{
                width: 12,
                height: 12,
                borderRadius: 999,
                background: swatch.hex,
                border: "1px solid rgba(0,0,0,0.08)",
              }}
            />
            <span className="text-[10px] font-semibold uppercase tracking-[0.08em] text-kin-muted">
              {swatch.label}
            </span>
            <code className="text-[10px] text-kin-body">{swatch.hex}</code>
          </span>
        ))}
      </div>

      <div className="mt-4">
        <BackgroundPreview option={option} />
      </div>
    </article>
  );
}

function AccentStudyCard({ study }) {
  return (
    <article
      style={{
        background: `${study.color}12`,
        border: `1px solid ${study.color}26`,
      }}
      className="rounded-2xl p-4"
    >
      <p
        style={{ color: study.color }}
        className="text-[10px] font-semibold uppercase tracking-[0.12em]"
      >
        {study.label}
      </p>
      <p className="mt-2 text-sm font-semibold text-kin-black">{study.title}</p>
      <p className="mt-2 text-xs leading-relaxed text-kin-body/85">{study.body}</p>
    </article>
  );
}

export default function ColorPalette() {
  return (
    <section
      id="colour"
      className="kin-section-glow border-t border-kin-olive/10 px-6 py-14 scroll-mt-16 sm:px-8 sm:py-16 gradient-wash"
    >
      <div className="mx-auto max-w-6xl">
        <p className="text-sm font-semibold tracking-[0.22em] uppercase text-kin-muted">
          02 - Colour Palette
        </p>

        <div className="mt-6 max-w-3xl">
          <h2 className="font-serif text-4xl font-bold text-kin-black sm:text-5xl">
            Precision in color
          </h2>
          <p className="mt-4 text-base leading-relaxed text-kin-body/90 sm:text-lg">
            The palette keeps the greens vivid, warms the base with parchment backgrounds, and uses
            accents as metadata rather than wallpaper. The result should feel premium, human, and
            clearly structured.
          </p>
        </div>

        <div className="mt-10 space-y-10">
          <ColorGroup title="Primary" swatches={colors.primary} />
          <ColorGroup title="Secondary" swatches={colors.secondary} />
          <ColorGroup title="Accents" swatches={colors.accents} />
          <ColorGroup title="Neutrals" swatches={colors.neutrals} />
          <ColorGroup title="Backgrounds" swatches={colors.backgrounds} />
        </div>

        <div className="mt-10 grid grid-cols-1 gap-4 lg:grid-cols-2">
          <article className="kin-surface-strong kin-surface-clouds rounded-3xl p-6">
            <p className="text-xs font-semibold tracking-[0.2em] uppercase text-kin-muted">
              Contrast Pairing
            </p>
            <div className="mt-4 space-y-3">
              <div className="rounded-2xl border border-kin-olive/12 bg-kin-white px-4 py-3">
                <p className="text-sm font-semibold text-kin-black">
                  Ink on Soft White is primary long-form pair.
                </p>
              </div>
              <div className="rounded-2xl bg-kin-green px-4 py-3 text-white">
                <p className="text-sm font-semibold">White on Kin Green is primary CTA pair.</p>
              </div>
              <div className="rounded-2xl bg-kin-gold px-4 py-3">
                <p className="text-sm font-semibold text-kin-black">
                  Ink on Sun Gold is the supportive guidance pair.
                </p>
              </div>
              <div className="rounded-2xl border border-kin-lavender/18 bg-kin-lavender-light px-4 py-3">
                <p className="text-sm font-semibold text-kin-black">
                  Ink on Lilac Mist frames AI and analysis context.
                </p>
              </div>
            </div>
          </article>

          <article className="kin-surface kin-surface-splash rounded-3xl p-6">
            <p className="text-xs font-semibold tracking-[0.2em] uppercase text-kin-muted">
              Usage Ratios
            </p>
            <div className="mt-5 space-y-3">
              {[
                { label: "Neutrals & backgrounds", value: 64, color: "bg-kin-olive/20" },
                { label: "Primary green family", value: 18, color: "bg-kin-green/70" },
                { label: "Warm base surfaces", value: 10, color: "bg-kin-gold/45" },
                { label: "Accents & trust states", value: 8, color: "bg-kin-lavender/55" },
              ].map((item) => (
                <div key={item.label}>
                  <div className="mb-1 flex items-center justify-between text-xs">
                    <span className="font-semibold text-kin-body">{item.label}</span>
                    <span className="text-kin-muted">{item.value}%</span>
                  </div>
                  <div className="h-2 rounded-full bg-kin-olive/10">
                    <div
                      className={`h-full rounded-full ${item.color}`}
                      style={{ width: `${item.value}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </article>
        </div>

        <article className="mt-10 kin-surface rounded-3xl p-6 sm:p-8">
          <p className="text-xs font-semibold tracking-[0.2em] uppercase text-kin-muted">
            Base Surface Decision
          </p>
          <p className="mt-3 max-w-3xl text-sm leading-relaxed text-kin-body/90">
            We explored five background bases. The chosen direction is parchment: warm enough to
            feel human and premium, but still clean enough to let the greens and structured data
            stay sharp.
          </p>

          <div className="mt-6 grid grid-cols-1 gap-4 xl:grid-cols-2">
            {backgroundOptions.map((option) => (
              <BackgroundOptionCard key={option.id} option={option} />
            ))}
          </div>
        </article>

        <article className="mt-10 kin-surface-strong rounded-3xl p-6 sm:p-8">
          <div className="max-w-3xl">
            <p className="text-xs font-semibold tracking-[0.2em] uppercase text-kin-muted">
              In Context
            </p>
            <h3 className="mt-2 font-serif text-3xl font-bold text-kin-black sm:text-4xl">
              Bright greens, cool neutrals, parchment base
            </h3>
            <p className="mt-3 text-sm leading-relaxed text-kin-body/90 sm:text-base">
              The system should not collapse into dark text plus green buttons. Parchment and soft
              white carry the structure, while lavender, gold, orange, and rose create visible
              moments of intelligence, guidance, review, and trust.
            </p>
          </div>

          <div className="mt-6 grid grid-cols-1 gap-4 xl:grid-cols-[1.15fr_0.85fr]">
            <div
              style={{
                background: selectedDirection.backgrounds.cream,
                border: "1px solid rgba(0,0,0,0.05)",
              }}
              className="rounded-[28px] p-5"
            >
              <div className="flex flex-wrap gap-2">
                <TonePill label="AI session" color={selectedDirection.accents.lavender} />
                <TonePill label="Doctor-ready" color={selectedDirection.accents.gold} />
                <TonePill label="Review needed" color={selectedDirection.accents.orange} />
              </div>

              <div
                style={{
                  background: selectedDirection.backgrounds.white,
                  border: "1px solid rgba(0,0,0,0.05)",
                }}
                className="mt-4 rounded-[24px] p-5"
              >
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-kin-muted">
                      Recovery Overview
                    </p>
                    <h4 className="mt-2 font-serif text-3xl font-bold text-kin-black">
                      Sleep Deep Dive
                    </h4>
                    <p className="mt-2 max-w-xl text-sm leading-relaxed text-kin-body/90">
                      A layered surface model: parchment page, warm linen section, soft white card,
                      then tinted metadata moments for AI, guidance, and trust.
                    </p>
                  </div>
                  <span className="rounded-full border border-kin-green/20 bg-kin-green/10 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.08em] text-kin-green-dark">
                    Ready to run
                  </span>
                </div>

                <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-3">
                  <div
                    style={{
                      background: `${selectedDirection.greens.primary}12`,
                      border: `1px solid ${selectedDirection.greens.primary}24`,
                    }}
                    className="rounded-2xl p-4"
                  >
                    <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-kin-green-dark">
                      Trend
                    </p>
                    <p className="mt-2 text-sm font-semibold text-kin-body">Consistency +12%</p>
                    <p className="mt-1 text-xs text-kin-muted">Baseline improving</p>
                  </div>

                  <div
                    style={{
                      background: `${selectedDirection.accents.gold}14`,
                      border: `1px solid ${selectedDirection.accents.gold}28`,
                    }}
                    className="rounded-2xl p-4"
                  >
                    <p
                      style={{ color: "var(--color-kin-gold-dark)" }}
                      className="text-[10px] font-semibold uppercase tracking-[0.12em]"
                    >
                      Guidance
                    </p>
                    <p className="mt-2 text-sm font-semibold text-kin-body">Doctor brief ready</p>
                    <p className="mt-1 text-xs text-kin-muted">Share only what matters</p>
                  </div>

                  <div
                    style={{
                      background: `${selectedDirection.accents.lavender}14`,
                      border: `1px solid ${selectedDirection.accents.lavender}28`,
                    }}
                    className="rounded-2xl p-4"
                  >
                    <p
                      style={{ color: selectedDirection.accents.lavender }}
                      className="text-[10px] font-semibold uppercase tracking-[0.12em]"
                    >
                      AI
                    </p>
                    <p className="mt-2 text-sm font-semibold text-kin-body">Follow-up prompts</p>
                    <p className="mt-1 text-xs text-kin-muted">Ask why your score shifted</p>
                  </div>
                </div>

                <div className="mt-5 flex flex-wrap gap-3">
                  <button className="rounded-xl bg-kin-green px-4 py-2 text-sm font-semibold text-white shadow-[0_8px_20px_-12px_rgba(47,181,106,0.8)]">
                    Run Guide
                  </button>
                  <button
                    style={{
                      background: selectedDirection.backgrounds.white,
                    }}
                    className="rounded-xl border border-kin-olive/14 px-4 py-2 text-sm font-semibold text-kin-body"
                  >
                    Share Brief
                  </button>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-1">
              {accentStudies.map((study) => (
                <AccentStudyCard key={study.label} study={study} />
              ))}
            </div>
          </div>
        </article>

        <div className="mt-10 kin-surface rounded-[24px] p-6 sm:p-7">
          <div className="flex items-start justify-between gap-4 mb-5">
            <div>
              <p className="text-xs font-semibold tracking-[0.2em] uppercase text-kin-muted">
                Color by role
              </p>
              <p className="mt-1 text-sm text-kin-body/80 max-w-2xl">
                Each color has one job in the platform. These assignments only hold if they're
                enforced consistently — see section 04 for the full model.
              </p>
            </div>
            <a
              href="#platform-skill"
              className="shrink-0 rounded-xl border border-kin-olive/14 bg-kin-white/70 px-4 py-2 text-xs font-semibold text-kin-body transition hover:bg-kin-white"
            >
              Section 04 →
            </a>
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {[
              {
                label: "Platform green",
                rule: "Interactive only",
                note: "Buttons, active states, confirmations. Never decorative.",
                dot: "bg-kin-green",
              },
              {
                label: "Lavender",
                rule: "AI signal only",
                note: "AI-generated content and synthesis. Skills may not repurpose it.",
                dot: "bg-kin-lavender",
              },
              {
                label: "Skill lead color",
                rule: "Faithfully rendered",
                note: "Tinted on home screen, full saturation inside the skill view.",
                dot: "bg-kin-muted",
              },
              {
                label: "Gold · Orange · Rose",
                rule: "Not in platform chrome",
                note: "System alerts or skill use only. Not in navigation or vault surfaces.",
                dot: "bg-kin-gold",
              },
            ].map((item) => (
              <div
                key={item.label}
                className="rounded-[18px] border border-kin-white/50 bg-kin-white/60 px-4 py-3 shadow-sm backdrop-blur-md"
              >
                <div className="flex items-center gap-2 mb-2">
                  <div className={`h-2 w-2 rounded-full shrink-0 ${item.dot}`} />
                  <p className="text-[10px] font-semibold tracking-[0.14em] uppercase text-kin-muted">
                    {item.label}
                  </p>
                </div>
                <p className="text-xs font-semibold text-kin-black mb-0.5">{item.rule}</p>
                <p className="text-[10px] leading-relaxed text-kin-body/70">{item.note}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
